import { useNavigate } from 'react-router-dom';
import type { AIAttentionItem, AttentionKind } from '../types/contracts';
import { timeAgo } from '../lib/format';

const KIND_META: Record<AttentionKind, { icon: string; label: string; cls: string }> = {
  hot_lead: { icon: 'ri-fire-line', label: 'عميل ساخن', cls: 'text-rose-600 bg-rose-50' },
  follow_up_due: { icon: 'ri-alarm-line', label: 'متابعة مستحقة', cls: 'text-amber-600 bg-amber-50' },
  support_escalation: { icon: 'ri-error-warning-line', label: 'تصعيد دعم', cls: 'text-red-600 bg-red-50' },
  purchase_intent: { icon: 'ri-shopping-cart-2-line', label: 'نية شراء', cls: 'text-emerald-600 bg-emerald-50' },
  demo_active: { icon: 'ri-flask-line', label: 'تجربة نشطة', cls: 'text-sky-600 bg-sky-50' },
  demo_expiring: { icon: 'ri-time-line', label: 'تجربة على وشك الانتهاء', cls: 'text-amber-600 bg-amber-50' },
  trial_expiring: { icon: 'ri-timer-line', label: 'فترة تجربة تنتهي قريبًا', cls: 'text-orange-600 bg-orange-50' },
  ai_failure: { icon: 'ri-error-warning-line', label: 'تعذر رد المساعد', cls: 'text-red-600 bg-red-50' },
};

const FALLBACK_META = { icon: 'ri-alert-line', label: 'تنبيه', cls: 'text-gray-600 bg-gray-50' };

const PRIORITY_CLS = {
  high: 'text-rose-700',
  medium: 'text-amber-600',
  low: 'text-sky-600',
};

export function AttentionQueue({ items }: { items: AIAttentionItem[] }) {
  const navigate = useNavigate();
  return (
    <section className="rounded-2xl bg-white border border-gray-100">
      <header className="px-5 py-4 border-b border-gray-50 flex items-center justify-between">
        <h2 className="text-sm font-black text-gray-900 flex items-center gap-2">
          <i className="ri-fire-fill text-rose-500" /> تحتاج انتباهًا
        </h2>
        <span className="text-[11px] text-gray-400">{items.length} مفتوحة</span>
      </header>
      <ul className="divide-y divide-gray-50">
        {items.map((it) => {
          const meta = KIND_META[it.kind] ?? FALLBACK_META;
          return (
            <li key={it.id} className="px-5 py-4 hover:bg-gray-50/60 transition-colors">
              <div className="flex items-start gap-3">
                <span className={`w-9 h-9 shrink-0 rounded-xl flex items-center justify-center text-base ${meta.cls}`}>
                  <i className={meta.icon} />
                </span>
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className={`text-[10px] font-black tracking-widest uppercase ${PRIORITY_CLS[it.priority]}`}>
                      {it.priority === 'high' ? 'أولوية عالية' : it.priority === 'medium' ? 'أولوية متوسطة' : 'أولوية منخفضة'}
                    </span>
                    <span className="text-[10px] text-gray-400">{meta.label}</span>
                  </div>
                  <p className="mt-1 text-sm font-bold text-gray-900">
                    {it.customer_name} — {it.business}
                  </p>
                  <p className="text-xs text-gray-600 truncate">{it.excerpt}</p>
                  <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-gray-500">
                    <span>الحالة: <b className="text-gray-700">{KIND_META[it.status_label as AttentionKind]?.label ?? FALLBACK_META.label}</b></span>
                    <span>آخر تفاعل: {timeAgo(it.last_interaction_at)}</span>
                    <span>ثقة المساعد: <b className="text-teal-700">{it.ai_confidence}%</b></span>
                  </div>
                  <p className="mt-1.5 text-[11px] text-gray-600">
                    الإجراء المقترح: <span className="font-bold text-gray-800">{it.suggested_action}</span>
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => it.conversation_id && navigate(`/intelligence/conversations/${it.conversation_id}`)}
                  className="shrink-0 self-center px-3 py-1.5 rounded-lg bg-gray-900 text-white text-[11px] font-bold hover:bg-gray-800"
                >
                  فتح المحادثة
                </button>
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
